import React, { useCallback } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import icon from './assets/icon.png';
import iphoneImage from './assets/iphone.png';
import LegalPage from './pages/LegalPage';
import OpenInAppPage from './pages/OpenInAppPage';
import { AppleIcon, PlayIcon } from './StoreIcons';
import { APP_SCHEME_URL, APP_STORE_URL, PLAY_STORE_URL, useDevice } from './appLinks';
import { LEGAL_DOCS } from './legal/legalContent';

const Home: React.FC = () => {
  const { isAndroid, isIOS, isMobile } = useDevice();

  const handleOpenApp = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const fallback = isIOS ? APP_STORE_URL : isAndroid ? PLAY_STORE_URL : undefined;
    const timer = fallback
      ? window.setTimeout(() => {
        window.location.href = fallback;
      }, 1100)
      : undefined;

    window.location.href = APP_SCHEME_URL;

    // Clear the fallback timer if navigation away occurs (user already has the app)
    const cancelFallback = () => {
      if (timer) {
        window.clearTimeout(timer);
      }
      document.removeEventListener('visibilitychange', cancelFallback);
    };

    document.addEventListener('visibilitychange', cancelFallback);
  }, [isAndroid, isIOS]);

  return (
    <div className="landing">
      <div className="orb orb-magenta" />
      <div className="orb orb-cyan" />

      <header className="top-bar">
        <div className="brand">
          <div className="brand-mark">
            <img src={icon} alt="Rushmore icon" />
          </div>
          <div className="brand-copy">
            <span className="brand-name">Rushmore Social</span>
            <span className="brand-tagline">Socially simple. Less influence, more favorites.</span>
          </div>
        </div>
        <nav className="top-nav">
          <a className="top-link" href="mailto:support@ptoallc.com">Contact</a>
          <Link className="top-link" to="/terms">Terms</Link>
          <Link className="top-link" to="/privacy">Privacy</Link>
        </nav>
      </header>

      <main className="hero-grid">
        <section className="hero-copy">
          <h1 className="headline">One home for your favorites.</h1>
          <p className="lede">
            Rushmore is where real favorites live. Build and share definitive Rushmores across sports, movies, restaurants, games, and more—without the noise, trend-chasing, and influence of the modern feed. Guided Rushmores make it simple, fast, and true to what you actually think.
          </p>

          <div className="cta-row">
            {isMobile && (
              <button className="btn primary" onClick={handleOpenApp}>
                Open Rushmore
              </button>
            )}
            <a
              className="btn ghost"
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
            >
              <span className="btn-content">
                <AppleIcon />
                <span>Download on iOS</span>
              </span>
            </a>
            <a
              className="btn ghost"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noreferrer"
            >
              <span className="btn-content">
                <PlayIcon />
                <span>Get it on Android</span>
              </span>
            </a>
          </div>


          <div className="pill-row">
            <span className="pill">Definitive, not disposable</span>
            <span className="pill">See what people really think</span>
            <span className="pill">Made for comparison</span>
          </div>
        </section>

        <section className="hero-visual">
          <div className="glass-card">
            <div className="device-top">
              <div className="signal-dot" />
              <div className="signal-dot" />
              <div className="signal-dot" />
            </div>

            <div className="app-icon-ring">
              <img src={icon} alt="Rushmore app icon" className="app-icon" />
            </div>

            <div className="list-card">
              <header className="list-card-header">
                <div>
                  <p className="list-label">Your Rushmore</p>
                  <h3 className="list-title">Tarantino Films</h3>
                </div>
              </header>
              <ul className="list-items">
                <li>
                  <span className="rank">1</span>
                  <span className="item">Inglourious Basterds</span>
                </li>
                <li>
                  <span className="rank">2</span>
                  <span className="item">Pulp Fiction</span>
                </li>
                <li>
                  <span className="rank">3</span>
                  <span className="item">Kill Bill Vol. 1</span>
                </li>
                <li>
                  <span className="rank">4</span>
                  <span className="item">Reservoir Dogs</span>
                </li>
              </ul>
            </div>

            {!isMobile && (
              <div className="phone-frame">
                <img src={iphoneImage} alt="Rushmore mobile preview" />
              </div>
            )}
          </div>
        </section>
      </main>

      <section className="feature-row">
        <div className="feature-card">
          <h3>Definitive, not disposable</h3>
          <p>Rushmore is built for favorites that last—not fleeting posts, passing trends, or whatever the feed is pushing today.</p>
        </div>
        <div className="feature-card">
          <h3>See what people really think</h3>
          <p>Explore the Rushmores of friends, creators, and public figures in one place—without digging through posts, clips, and noise.</p>
        </div>
        <div className="feature-card">
          <h3>Made for comparison</h3>
          <p>Compare lists, settle debates, and keep the receipts on what people actually stand by.</p>
        </div>

      </section>

      <footer className="site-footer">
        <p>
          © {new Date().getFullYear()} PTOA LLC. Rushmore is a product of PTOA LLC.
        </p>
        <p className="footer-legal">
          {LEGAL_DOCS.map((doc) => (
            <Link key={doc.slug} className="footer-link" to={`/${doc.slug}`}>
              {doc.title}
            </Link>
          ))}
        </p>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        {LEGAL_DOCS.map((doc) => (
          <Route key={doc.slug} path={`/${doc.slug}`} element={<LegalPage doc={doc} />} />
        ))}
        <Route path="/ur/:id" element={<OpenInAppPage kind="ur" />} />
        <Route path="/u/:id" element={<OpenInAppPage kind="u" />} />
        <Route path="/r/:id" element={<OpenInAppPage kind="r" />} />
        {/* Unknown paths used to render nothing at all — a blank page. */}
        <Route path="*" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
